import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { type TextFieldEvent } from '@volvo/vcdk';
import { Button, Switch, TextField, Typography } from '@volvo/vcdk-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ICommunicationPreference } from '@/service/vpc/types/IEndUser';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { analyticsEvents } from '@/contexts/analytics/events';
import { useFleetAccount } from '@/contexts/fleet-account/useFleetAccount';
import { getEndUserQueryOptions, queryKeys } from '@/service/vpc/queryOptions';
import { vpcService } from '@/service/vpc/vpcService';

export const Route = createFileRoute('/_authenticated/settings/')({
  component: SettingsPage,
  errorComponent: ErrorBoundary,
  loader: (opts) => {
    return opts.context.queryClient.ensureQueryData(getEndUserQueryOptions());
  },
});

function SettingsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { selectedFleetAccount: fleetAccount } = useFleetAccount();
  const { data: endUser } = useQuery(getEndUserQueryOptions());

  // const canEditFleetName = userRoles.some((role) => role.role === 'Owner' || role.role === 'Manager');
  const [fleetName, setFleetName] = useState(fleetAccount?.name || '');

  const [entityVersionFleetAccount, setEntityVersionfleetAccount] = useState(
    parseInt(fleetAccount?.entityVersion || '0')
  );
  const [entityVersionEndUser, setEntityVersionEndUser] = useState(parseInt(endUser?.entityVersion || '0'));

  const { mutateAsync: updateFleetAccount, isPending } = useMutation({
    mutationFn: ({
      fleetAccountId,
      name,
      entityVersion,
    }: {
      fleetAccountId: string;
      name: string;
      entityVersion: number;
    }) =>
      vpcService.FleetAccount.updateFleetAccount({
        fleetAccountId,
        name,
        entityVersion,
      }),
    onSuccess(data) {
      setEntityVersionfleetAccount(data);
      queryClient.invalidateQueries({ queryKey: [queryKeys.getFleetAccounts], exact: false });
      queryClient.invalidateQueries({ queryKey: [queryKeys.getEndUser], exact: false });
    },
  });

  const { mutateAsync: updateEndUserCommunicationPreference, isPending: isUpdatingEndUserCommunicationPreference } =
    useMutation({
      mutationFn: ({
        method,
        endUserId,
        preference,
        entityVersion,
      }: {
        method: 'add' | 'remove';
        endUserId: string;
        preference: ICommunicationPreference;
        entityVersion: number;
      }) =>
        method === 'remove'
          ? vpcService.FleetAccount.removeEndUserCommunicationPreference({
              endUserId,
              preference,
              entityVersion,
            })
          : vpcService.FleetAccount.addEndUserCommunicationPreference({
              endUserId,
              preference,
              entityVersion,
            }),
      onSuccess(data) {
        setEntityVersionEndUser(data);
        queryClient.invalidateQueries({ queryKey: [queryKeys.getEndUser], exact: false });
      },
    });

  const isSaveDisabled =
    !fleetAccount?.fleetAccountId || isPending || fleetName.trim() === fleetAccount?.name?.trim() || !fleetName.trim();

  return (
    <div className="flex flex-col gap-9">
      <Typography variant="heading3">{t(($) => $.pages.settings.application_settings)}</Typography>
      <div className="grid grid-cols-12 items-end gap-6">
        <div className="col-span-6">
          <TextField
            id="fleetAccountName"
            label={t(($) => $.pages.settings.account_name)}
            value={fleetName}
            onInput={(e: TextFieldEvent) => setFleetName(e.detail.value)}
          />
        </div>
        <div className="col-span-6">
          <Button
            variant="secondary"
            loading={isPending}
            disabled={isSaveDisabled}
            onClick={async () => {
              await updateFleetAccount({
                fleetAccountId: fleetAccount?.fleetAccountId || '',
                name: fleetName,
                entityVersion: entityVersionFleetAccount,
              });
            }}
          >
            {t(($) => $.common.save)}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-12 items-end gap-6">
        <div className="col-span-full">
          <Typography variant="large-body">{t(($) => $.pages.settings.notifications)}</Typography>
        </div>
        <div className="col-span-6 flex flex-col gap-6">
          <TextField
            id="notificationEmail"
            disabled
            label={t(($) => $.pages.settings.email)}
            value={endUser?.email || ''}
          />
          <div className="flex gap-5">
            <Switch
              checked={endUser?.communicationPreferences.includes('Email')}
              disabled={isUpdatingEndUserCommunicationPreference}
              onChange={() => {
                analyticsEvents.Interaction.componentInteraction({
                  parent: 'SettingsPage',
                  type: 'Switch',
                  title: 'Email Notifications',
                  placement: 'SettingsPage',
                  selectedOptions: [
                    {
                      attribute: 'preference',
                      value: 'Email',
                    },
                    {
                      attribute: 'method',
                      value: endUser?.communicationPreferences.includes('Email') ? 'remove' : 'add',
                    },
                  ],
                });
                updateEndUserCommunicationPreference({
                  method: endUser?.communicationPreferences.includes('Email') ? 'remove' : 'add',
                  endUserId: endUser?.endUserId || '',
                  preference: 'Email',
                  entityVersion: entityVersionEndUser,
                });
              }}
            />
            <Typography variant="caption1" className="">
              {t(($) => $.pages.settings.email_notifications)}
            </Typography>
          </div>
        </div>
        <div className="col-span-6 flex flex-col gap-6">
          <TextField
            id="notificationSMS"
            disabled
            label={t(($) => $.pages.settings.sms)}
            value={endUser?.phoneNumber?.mobilePhoneNumber || ''}
          />
          <div className="flex gap-5">
            <Switch
              checked={endUser?.communicationPreferences.includes('SMS')}
              disabled={isUpdatingEndUserCommunicationPreference}
              onChange={() => {
                analyticsEvents.Interaction.componentInteraction({
                  parent: 'SettingsPage',
                  type: 'Switch',
                  title: 'SMS Notifications',
                  placement: 'SettingsPage',
                  selectedOptions: [
                    {
                      attribute: 'preference',
                      value: 'SMS',
                    },
                    {
                      attribute: 'method',
                      value: endUser?.communicationPreferences.includes('SMS') ? 'remove' : 'add',
                    },
                  ],
                });
                updateEndUserCommunicationPreference({
                  method: endUser?.communicationPreferences.includes('SMS') ? 'remove' : 'add',
                  endUserId: endUser?.endUserId || '',
                  preference: 'SMS',
                  entityVersion: entityVersionEndUser,
                });
              }}
            />
            <Typography variant="caption1" className="">
              {t(($) => $.pages.settings.sms_notifications)}
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
}
